import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    MessageCircle,
    Activity,
    Map as MapIcon,
    FileSignature,
    User as UserIcon,
    LifeBuoy,
    Sparkles,
    ArrowRight,
    ArrowLeft,
    X,
    Check,
    Stethoscope,
    Compass,
    FolderOpen,
    FileText,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  TourWizard — offered on first login. Two phases:                          */
/*    1. Prompt: "Want a quick tour?" with Yes / Later / No thanks            */
/*    2. Carousel: ~9 calm cards explaining each section, with a final CTA    */
/*  Card order tracks the v3 sidebar (dashboard → roadmap → guide etc).        */
/* -------------------------------------------------------------------------- */

const TOUR_STEPS = [
    {
        icon: Sparkles,
        title: "Welcome to Memory Lane",
        body: "Memory Lane is your GPS for dementia caregiving — not a checklist. Take a breath. This 60-second tour shows you where everything lives. You can skip at any time.",
        tone: "teal",
        cta: null,
    },
    {
        icon: MapIcon,
        title: "Your roadmap",
        body: "A compassionate guide through dementia caregiving — starting from \"Noticing changes\" before any diagnosis, all the way through advanced care. Recommendations are grouped by life area (medical, legal, daily care, end-of-life) with gentle statuses like \"Already in place\" or \"Working on it.\" No checklist, no overdue items — just a guide that walks alongside you.",
        tone: "sage",
        cta: { to: "/roadmap", label: "See the roadmap" },
    },
    {
        icon: UserIcon,
        title: "Their profile",
        body: "Medications, doctors, allergies, emergency contacts, insurance. The one-page sheet a paramedic can read in 10 seconds. Autosaves as you type.",
        tone: "sand",
        cta: { to: "/patient", label: "Open the profile" },
    },
    {
        icon: FolderOpen,
        title: "Documents",
        body: "Plain-English breakdown of MPOA, DPOA, Advance Directive, Living Will, and POLST — plus a place to upload signed copies and share medications with any new clinician.",
        tone: "cream",
        cta: { to: "/documents", label: "Open Documents" },
    },
    {
        icon: Activity,
        title: "Behavior log + Lane (AI)",
        body: "Track behaviors as they happen. Lane — your AI care companion — notices patterns and suggests what's worked for other families. Vent, ask a question, work through a hard decision.",
        tone: "teal",
        cta: { to: "/behavior", label: "Open the log" },
    },
    {
        icon: FileSignature,
        title: "Hard talks · Share with family",
        body: "Scripts for the talks no one prepared you for — the driving conversation, MPOA paperwork, family conflict. Plus a private way to share the situation with siblings, so everyone's on the same page.",
        tone: "lavender",
        cta: { to: "/conversations", label: "See the scripts" },
    },
    {
        icon: FolderOpen,
        title: "Knowledge Library",
        body: "90+ evidence-informed guides — early-stage planning, behaviors, Medicare, hard conversations, late-stage care — grouped by topic with a search bar so the right answer is one tap away. Lives inside the Learn page and at /library.",
        tone: "cream",
        cta: { to: "/education#knowledge-library", label: "Browse the library" },
    },
    {
        icon: Compass,
        title: "Care Navigator",
        body: "Live 1:1 video sessions with a trained dementia nurse practitioner. The first 15 minutes are free — no card needed. Longer 30 or 60-minute sessions available when you need them.",
        tone: "coral",
        cta: { to: "/navigator", label: "Meet the navigator" },
    },
    {
        icon: Stethoscope,
        title: "GUIDE program · US-only",
        body: "Medicare's GUIDE model funds dementia care navigation. Check eligibility, fill the pre-visit screeners at your pace, then send one private link to your GUIDE provider — no faxing, no retyping.",
        tone: "sage",
        cta: { to: "/guide", label: "Check GUIDE eligibility" },
    },
    {
        icon: FileText,
        title: "Note to the care team",
        body: "Build a calm, structured note from everything you've already logged — meds, behaviors, what's helped, GUIDE scores. Send a single read-only link to your loved one's doctor or care team.",
        tone: "teal",
        cta: { to: "/care-team-summary", label: "Open the note" },
    },
    {
        icon: LifeBuoy,
        title: "Find help nearby",
        body: "Support groups, respite care, legal aid, hospice — searchable by state. Plus GUIDE-participating providers, national resources (Alzheimer's Helpline, 988, Eldercare Locator), and community partners.",
        tone: "cream",
        cta: { to: "/help-finder", label: "Browse resources" },
    },
    {
        icon: Check,
        title: "You're ready",
        body: "Take what helps. Leave the rest. Your dashboard greets you with a soft quote and a few calm tiles. Lane and the care navigator are always one tap away. We're here whenever you need us.",
        tone: "teal",
        cta: null,
    },
];

const TONE_BG = {
    teal: "bg-teal-soft",
    coral: "bg-coral-light/50",
    sage: "bg-stage_early/50",
    lavender: "bg-stage_late/50",
    sand: "bg-stage_middle/50",
    cream: "bg-cream-dark/60",
};
const TONE_ICON = {
    teal: "text-teal-dark",
    coral: "text-coral-dark",
    sage: "text-stage_early-dark",
    lavender: "text-stage_late-dark",
    sand: "text-stage_middle-dark",
    cream: "text-[#4C5551]",
};

export default function TourWizard() {
    const { user, refresh } = useAuth();
    const isTester = !!(user && user !== false && user.tester_code);
    const [phase, setPhase] = useState("prompt"); // prompt | tour | closed
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    // Tracks whether we've already claimed a "seen once" state for this
    // fresh user so the effect below doesn't fire twice under StrictMode.
    const seenClaimedRef = React.useRef(false);

    // Testers: skip the opt-in prompt and start the carousel directly. We
    // derive this during render (instead of setState-in-effect) because
    // `user` is null at mount while auth is loading — so a useState
    // initializer would freeze the wrong value. As long as the tester
    // hasn't already finished/dismissed the tour, promote "prompt" → "tour".
    const effectivePhase = (
        isTester
        && phase === "prompt"
        && user?.tour_state !== "completed"
        && user?.tour_state !== "dismissed"
        && user?.tour_state !== "later"
        && user?.tour_state !== "seen"
    ) ? "tour" : phase;

    // Persist a "seen" state the very first time the prompt is rendered
    // for a fresh user. This closes the door on the "popup keeps
    // appearing on every login" bug: if the user closes the tab or the
    // browser without clicking anything, we've already marked the tour
    // as seen so the next session skips the prompt.
    // Only fires for approved, non-admin, non-tester users whose
    // tour_state is still empty. We DON'T flip phase here — the user
    // should still see the prompt this session; we only prevent future
    // re-appearances.
    React.useEffect(() => {
        if (!user || user === false) return;
        if (user.role === "admin") return;
        if (user.status !== "approved") return;
        if (isTester) return;                     // tester flow already forced to "tour"
        if (user.tour_state) return;              // already recorded some state
        if (seenClaimedRef.current) return;       // already claimed this mount
        seenClaimedRef.current = true;
        // Fire-and-forget — errors here are non-fatal.
        api.post("/auth/tour-state", { state: "seen" }).catch(() => {});
    }, [user, isTester]);

    // Only show for approved users who haven't completed, dismissed,
    // deferred with "later", or been marked "seen" (auto-claimed on first
    // render). Anything else means we've never shown them the tour yet.
    if (!user || user === false || user.role === "admin") return null;
    if (user.status !== "approved") return null;
    if (
        user.tour_state === "completed"
        || user.tour_state === "dismissed"
        || user.tour_state === "later"
        || user.tour_state === "seen"
    ) return null;
    if (effectivePhase === "closed") return null;

    const saveState = async (state) => {
        setSubmitting(true);
        try {
            await api.post("/auth/tour-state", { state });
            await refresh(); // re-fetch user so tour_state is in context and modal stays closed
        } catch {
            /* even if save fails, close the prompt — we don't want to nag */
        } finally {
            setSubmitting(false);
            setPhase("closed");
        }
    };

    /* ----- PHASE 1: opt-in prompt ----- */
    if (effectivePhase === "prompt") {
        const firstName = (user.name || "").split(" ")[0] || "";
        return (
            <Backdrop>
                <div
                    data-testid="tour-prompt"
                    className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-7"
                >
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 w-11 h-11 rounded-2xl bg-teal-soft flex items-center justify-center">
                                <Sparkles strokeWidth={1.6} className="text-teal-dark" />
                            </div>
                            <div>
                                <h2 className="text-xl md:text-2xl font-heading font-semibold tracking-tight">
                                    {firstName ? `Welcome, ${firstName}.` : "Welcome."}
                                </h2>
                                <p className="mt-1 text-sm text-[#4C5551]">
                                    Memory Lane has a few rooms — Talk to Lane, your roadmap, hard conversations, resources, and more. Want a quick tour of where everything lives?
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            aria-label="Close"
                            data-testid="tour-prompt-close"
                            onClick={() => saveState("later")}
                            className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                        >
                            <X size={16} strokeWidth={1.8} />
                        </button>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2 justify-end">
                        <button
                            type="button"
                            onClick={() => saveState("dismissed")}
                            disabled={submitting}
                            data-testid="tour-prompt-no"
                            className="text-sm font-medium text-[#4C5551] hover:bg-cream-dark rounded-2xl px-4 py-2 disabled:opacity-60"
                        >
                            No thanks
                        </button>
                        <button
                            type="button"
                            onClick={() => saveState("later")}
                            disabled={submitting}
                            data-testid="tour-prompt-later"
                            className="text-sm font-medium text-[#4C5551] hover:bg-cream-dark rounded-2xl px-4 py-2 disabled:opacity-60"
                        >
                            Maybe later
                        </button>
                        <button
                            type="button"
                            onClick={() => { setPhase("tour"); setStep(0); }}
                            data-testid="tour-prompt-yes"
                            className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-5 py-2 text-sm font-medium"
                        >
                            <Sparkles size={14} strokeWidth={1.8} /> Show me around
                        </button>
                    </div>
                    <p className="mt-3 text-xs text-[#6E7A74] text-right">60 seconds. Skip anytime.</p>
                </div>
            </Backdrop>
        );
    }

    /* ----- PHASE 2: the tour itself ----- */
    const totalSteps = TOUR_STEPS.length;
    const s = TOUR_STEPS[step];
    const Icon = s.icon;
    const isLast = step === totalSteps - 1;
    const isFirst = step === 0;

    return (
        <Backdrop>
            <div
                data-testid="tour-carousel"
                className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 md:p-7"
            >
                <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">
                        Step {step + 1} of {totalSteps}
                    </p>
                    <button
                        type="button"
                        aria-label="Skip the tour"
                        data-testid="tour-skip"
                        onClick={() => saveState("dismissed")}
                        className="text-xs font-medium text-[#6E7A74] hover:text-[#4C5551]"
                    >
                        Skip
                    </button>
                </div>

                {/* Progress dots */}
                <div className="flex gap-1.5 mb-5" data-testid="tour-progress">
                    {TOUR_STEPS.map((_, i) => (
                        <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-teal-dark" : i < step ? "w-1.5 bg-teal" : "w-1.5 bg-cream-dark"}`}
                        />
                    ))}
                </div>

                {/* Step content */}
                <div className={`ml-card !border-transparent p-5 md:p-6 ${TONE_BG[s.tone] || TONE_BG.cream}`}>
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                            <Icon strokeWidth={1.6} size={22} className={TONE_ICON[s.tone] || TONE_ICON.cream} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-xl md:text-2xl font-heading font-semibold text-[#12776F]">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-base text-[#1F2624] leading-relaxed">
                                {s.body}
                            </p>
                            {s.cta && (
                                <Link
                                    to={s.cta.to}
                                    data-testid={`tour-cta-${step}`}
                                    onClick={() => { saveState("completed"); }}
                                    className="mt-4 inline-flex items-center gap-1.5 bg-white hover:bg-cream-dark border border-[#E2E8F0] text-teal-dark rounded-2xl px-4 py-2 text-sm font-medium"
                                >
                                    {s.cta.label} <ArrowRight size={14} strokeWidth={1.8} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* Nav row */}
                <div className="mt-5 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => setStep(s => Math.max(0, s - 1))}
                        disabled={isFirst}
                        data-testid="tour-back"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:bg-cream-dark disabled:opacity-30 disabled:hover:bg-transparent rounded-2xl px-3 py-2"
                    >
                        <ArrowLeft size={14} strokeWidth={1.8} /> Back
                    </button>
                    {!isLast ? (
                        <button
                            type="button"
                            onClick={() => setStep(s => Math.min(totalSteps - 1, s + 1))}
                            data-testid="tour-next"
                            className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-5 py-2 text-sm font-medium"
                        >
                            Next <ArrowRight size={14} strokeWidth={1.8} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => saveState("completed")}
                            disabled={submitting}
                            data-testid="tour-done"
                            className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-5 py-2 text-sm font-medium disabled:opacity-60"
                        >
                            <Check size={14} strokeWidth={2} /> I&apos;m ready
                        </button>
                    )}
                </div>
            </div>
        </Backdrop>
    );
}

function Backdrop({ children }) {
    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in"
            data-testid="tour-backdrop"
        >
            {children}
        </div>
    );
}
